/**
 * Builds a thumbnail URL for GPU-accelerated display of large images.
 *
 * Appends width/quality query params for CDNs that support on-the-fly resizing
 * (e.g., Cloudflare Images, imgix, CloudFront + Lambda@Edge).
 * If the CDN does not support them the params are ignored and the full image
 * loads; the caller should still apply GPU CSS hints (will-change, translateZ)
 * so the browser composites the downscaled image on the GPU layer.
 *
 * @param url    Original image URL
 * @param width  Desired pixel width (2x for retina)
 * @param quality JPEG/WebP quality 1-100 (default 80)
 */
export function getThumbnailUrl(
  url: string | undefined | null,
  width: number,
  quality = 80,
): string {
  if (!url) return '';

  // Double the width for retina screens
  const retinaWidth = width * 2;

  try {
    const parsed = new URL(url);
    parsed.searchParams.set('w', String(retinaWidth));
    parsed.searchParams.set('q', String(quality));
    parsed.searchParams.set('fit', 'cover');
    return parsed.toString();
  } catch {
    // Non-absolute URL — return as-is
    return url;
  }
}

/**
 * CSS string for GPU-accelerated image thumbnails.
 * Apply to any <img> element that displays a large source at small dimensions.
 */
export const gpuThumbnailCss = `
  will-change: transform;
  transform: translateZ(0);
  image-rendering: auto;
  backface-visibility: hidden;
`;
